import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatRelativeTime } from '~/lib/time';
import { apiGetHandedOffChat, type HandedOffChat } from '~/lib/api/claims';

/*
 * Read-only archive of a handed-off generation (admin tracking). After a claim
 * the chat belongs to the claimant, but the admin who built it keeps this
 * window: the full transcript, the static preview, and a can't-miss "Claimed"
 * banner. Deliberately view-only — no composer, no sandbox, no workbench —
 * because acting on someone else's project from here would be a bug, not a
 * feature. Reached from the sidebar's "Handed off" rows.
 */

/** Pull readable text out of a stored UIMessage (string or parts array). */
function messageText(msg: any): string {
  const content = msg?.content ?? msg?.parts;

  if (typeof content === 'string') {
    return content;
  }

  if (Array.isArray(content)) {
    return content
      .filter((p: any) => p?.type === 'text' && typeof p.text === 'string')
      .map((p: any) => p.text)
      .join('\n')
      .trim();
  }

  return '';
}

export default function HandedOffChatPage() {
  const { chatId } = useParams<{ chatId: string }>();
  const [data, setData] = useState<HandedOffChat | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!chatId) {
      setError(true);
      setLoading(false);

      return;
    }

    apiGetHandedOffChat(chatId)
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [chatId]);

  const who = data?.claimedBy?.displayName || data?.claimedBy?.email || 'a user';

  return (
    <div className="h-full w-full overflow-y-auto bg-page transition-theme modern-scrollbar">
      <div className="mx-auto w-[min(920px,100%)] px-5 py-8">
        {/* Back to workspace */}
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-1.5 text-[12.5px] text-muted no-underline transition-colors hover:text-primary"
        >
          <span className="i-ph:arrow-left text-[12px]" /> Back to workspace
        </Link>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="i-svg-spinners:90-ring-with-bg text-2xl text-muted" />
          </div>
        ) : error || !data ? (
          <div className="py-24 text-center">
            <h1 className="m-0 mb-2 text-[20px] font-semibold text-primary">Handoff not found</h1>
            <p className="m-0 text-[13.5px] text-secondary">This generation may have been deleted by its new owner.</p>
          </div>
        ) : (
          <>
            {/* The can't-miss claimed banner — front and center */}
            <div
              className="mb-7 rounded-2xl border border-accent/40 bg-surface px-6 py-5 text-center"
              style={{ backgroundImage: 'linear-gradient(180deg, rgba(217,242,106,0.07), transparent 60%)' }}
            >
              <div className="mb-1.5 inline-flex items-center gap-2 font-geist-mono text-[10.5px] uppercase tracking-[0.15em] text-accent">
                <span className="i-ph:hand-coins text-[12px]" /> Handed off
              </div>
              <h1 className="ui-serif m-0 mb-1 text-[28px] leading-[1.15] text-primary">
                {data.description || 'Untitled generation'}
              </h1>
              <p className="m-0 text-[13.5px] text-secondary">
                Claimed by <span className="font-semibold text-accent">{who}</span>
                {data.claimedAt ? ` ${formatRelativeTime(data.claimedAt)}` : ''} — it now lives in their account. This
                is your read-only copy.
              </p>
            </div>

            {/* Static preview of the site as it was */}
            {data.hasPreview && (
              <div className="mb-7 overflow-hidden rounded-2xl border border-hairline bg-surface">
                <div className="flex items-center gap-2 border-b border-hairline px-4 py-2.5">
                  <span className="i-ph:globe-simple text-[13px] text-muted" />
                  <span className="ui-caption">Site preview</span>
                </div>
                <div className="relative" style={{ aspectRatio: '16 / 9' }}>
                  <iframe
                    src={`/p/${data.chatId}/?embed=1&static=1`}
                    title="Site preview"
                    className="absolute inset-0 h-full w-full border-0"
                  />
                </div>
              </div>
            )}

            {/* Transcript */}
            <div className="overflow-hidden rounded-2xl border border-hairline bg-surface">
              <div className="flex items-center gap-2 border-b border-hairline px-4 py-2.5">
                <span className="i-ph:chat-circle-text text-[13px] text-muted" />
                <span className="ui-caption">Conversation</span>
              </div>
              <div className="flex flex-col gap-4 px-5 py-5">
                {(data.messages as any[]).map((msg, i) => {
                  const text = messageText(msg);

                  if (!text) {
                    return null;
                  }

                  const isUser = msg?.role === 'user';

                  return (
                    <div key={msg?.id ?? i} className={isUser ? 'self-end max-w-[80%]' : 'self-start max-w-[85%]'}>
                      <div className="mb-1 font-geist-mono text-[10px] uppercase tracking-[0.1em] text-muted">
                        {isUser ? 'User' : 'Dunea'}
                      </div>
                      <div
                        className={
                          isUser
                            ? 'rounded-xl rounded-tr-sm bg-raised border border-hairline px-3.5 py-2.5 text-[13.5px] leading-[1.55] text-primary whitespace-pre-wrap [overflow-wrap:anywhere]'
                            : 'text-[13.5px] leading-[1.6] text-secondary whitespace-pre-wrap [overflow-wrap:anywhere]'
                        }
                      >
                        {text}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
